import { useSuspenseQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { useConditionalRoleQuery } from '#api/roles.queries.ts';
import { maxRoutesQueryOptions, useSuspenseRoutesDetailsQueries } from '#api/routes.queries.ts';
import { useSuspenseTagsDetailsQueries } from '#api/tags.queries.ts';
import { AlertIcon } from '#assets/ReactSvg.ts';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import type { Host } from '#dn-types/host.ts';
import type { FirewallRule } from '#dn-types/role.ts';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { FirewallRuleList } from '#screens/roles/detail/firewall-rule-list/FirewallRuleList.tsx';
import { sortFirewallRules } from '#screens/roles/detail/firewall-rule-list/sortFirewallRules.ts';
import { mapFirewallRulesToRoleForm } from '#screens/roles/detail/form/mapFirewallRulesToRoleForm.ts';
import type { FirewallRuleSource } from '#screens/roles/detail/types.ts';
import { mapFirewallRulesToFormData } from '#screens/routes/edit/mapRouteToForm.ts';
import { CIDRFirewallRulesList } from '#screens/routes/form/firewall-rule/CIDRFirewallRulesList.tsx';
import styles from './FirewallCard.module.css';

type Props = {
  host: Pick<Host, 'roleID' | 'id' | 'networkID' | 'isLighthouse' | 'isRelay' | 'tags'>;
  className?: string;
};

export function FirewallCard(props: Props) {
  return (
    <ErrorBoundary
      fallbackRender={() => {
        return (
          <DetailCard className={cn(styles.FirewallCard, props.className)}>
            <h2 className="h4 mb-16">Inbound firewall rules</h2>
            <p className="p-md">
              <AlertIcon /> Problem retrieving firewall rules
            </p>
          </DetailCard>
        );
      }}
    >
      <FirewallCardInner {...props} />
    </ErrorBoundary>
  );
}

/** A firewall rule source (role or tag) and the rules it contributes. */
type RuleSourceBlock = { source: FirewallRuleSource; rules: FirewallRule[] };

function FirewallCardInner({ host, className }: Props) {
  // Derived narrow threshold: this page wraps the card in
  // 312px of chrome above --size-breakpoint-s (200 sidebar + 2x40 body padding + 2x16 card
  // padding) and 64px below it. The full inbound table, with 60px of allowed-hosts room for the
  // host count, sums to 360 + 80 + 100 + 150 + 160 = 850, which stops fitting below 850 + 312 =
  // 1162 of viewport. The narrow tier shrinks the two prose columns (300 allowed hosts, 130 source)
  // so every column stays on screen down to 760 + 312 = 1072, and to 760 + 64 = 824 once the
  // sidebar collapses; below that the card scrolls sideways. Columns are never hidden: a rule row
  // only reads as a rule with source, destination, and ports all on show. The widths come from
  // FirewallRuleList's columns, so recompute this when they change.
  const narrow = useMatchMedia('(max-width: 1161px)');
  const { data: { data: role } = {}, isFetching: isFetchingRole } = useConditionalRoleQuery(host.roleID ?? '', {
    enabled: Boolean(host.roleID),
  });
  const roleFirewallRules = role?.firewallRules ?? [];

  // Tags can carry their own firewall rules. We hit the tag detail endpoint per tag because the
  // tags list omits firewall rules.
  const tagDetails = useSuspenseTagsDetailsQueries(host.tags).map((res) => res.data.data);

  const routerForRoutes = useSuspenseQuery({
    ...maxRoutesQueryOptions,
    select({ data }) {
      return { data: data.filter((route) => route.routerHostID === host.id) };
    },
  }).data.data;

  const routes = useSuspenseRoutesDetailsQueries(routerForRoutes.map((r) => r.id)).map((res) => res.data.data);

  const routeFirewallRules = routes.flatMap((route) => route.firewallRules);

  if (isFetchingRole) {
    return (
      <DetailCard className={cn(styles.FirewallCard, className)}>
        <LoadingSpinner />
      </DetailCard>
    );
  }

  // The incoming firewall rules effective on this host come from its role and its tags. Build one
  // block per contributing source, then annotate each rule with its source.
  const sourceBlocks: RuleSourceBlock[] = [];
  if (role && roleFirewallRules.length) {
    sourceBlocks.push({
      source: { kind: 'role', label: role.name, to: `/roles/${role.id}` },
      rules: roleFirewallRules,
    });
  }
  for (const tag of tagDetails) {
    const rules = tag.firewallRules ?? [];
    if (rules.length) {
      sourceBlocks.push({
        source: { kind: 'tag', label: tag.name, to: `/tags/${encodeURIComponent(tag.name)}` },
        rules,
      });
    }
  }

  // Always annotate, so the table renders the same columns/styling regardless of how many sources
  // happen to contribute — e.g. a host whose only rules come from a single tag still shows that tag
  // as the source.
  const incomingRules = sourceBlocks.flatMap((block, blockIndex) => {
    // Add a stable id per row (the list/table key), unique across sources, mirroring RHF fields.
    const rows = sortFirewallRules(
      mapFirewallRulesToRoleForm(block.rules).map((rule, index) => ({ ...rule, id: `${blockIndex}-${index}` }))
    );
    return rows.map((rule) => ({ ...rule, source: block.source }));
  });

  const hostNoun =
    host.isLighthouse ? 'lighthouse'
    : host.isRelay ? 'relay'
    : 'host';

  // Tags can carry firewall rules, so a host's reachability doesn't depend on its role alone and the
  // empty state stays source-neutral rather than blaming a missing role. It's scoped to inbound and
  // direct connections because a route firewall rules table may render right below it.
  const noRulesMessage = `No inbound firewall rules apply to this ${hostNoun}, so it will not allow direct connections from other hosts.`;

  return (
    <DetailCard className={cn(styles.FirewallCard, className)}>
      {incomingRules.length ?
        <div>
          <h2 className="h4 mb-8">Inbound firewall rules</h2>
          <p className="p-md mb-16">
            Which hosts are allowed to connect to this host, and the firewall rule that grants it. Click a role or tag
            to make changes.
          </p>
          <FirewallRuleList
            className={styles.FirewallCard_table}
            firewallRules={incomingRules}
            narrow={narrow}
            networkID={host.networkID}
          />
        </div>
      : <div>
          <h2 className="h4 mb-16">Inbound firewall rules</h2>
          <p className="p-md mb-0">{noRulesMessage}</p>
        </div>
      }

      {routeFirewallRules.length ?
        <div>
          <h2 className="h4 mb-8">Route firewall rules</h2>
          <p className="p-md mb-16">
            Which hosts are allowed to reach subnets through this host as a router, and the route firewall rule that
            grants it.
          </p>
          <CIDRFirewallRulesList
            className={styles.FirewallCard_table}
            firewallRules={mapFirewallRulesToFormData(routeFirewallRules)}
            networkID={host.networkID}
          />
        </div>
      : null}
    </DetailCard>
  );
}
